import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { solutionWorkloadKeys, systemConfigKeys } from "@/lib/query";

interface HoursPerIntensityPointResponse {
  value: number;
}

async function fetchHoursPerIntensityPoint(): Promise<HoursPerIntensityPointResponse> {
  const res = await fetch("/api/admin/system-config/hours-per-intensity-point");
  if (!res.ok) throw new Error("hours-per-intensity-point unavailable");
  return res.json();
}

/** Story 6: the admin-managed hours-per-Intensity-point constant (default 40). */
export function useHoursPerIntensityPoint() {
  return useQuery<HoursPerIntensityPointResponse>({
    queryKey: systemConfigKeys.hoursPerIntensityPoint(),
    queryFn: fetchHoursPerIntensityPoint,
    staleTime: 5 * 60 * 1000,
  });
}

async function updateHoursPerIntensityPoint(
  value: number,
): Promise<HoursPerIntensityPointResponse> {
  const res = await fetch("/api/admin/system-config/hours-per-intensity-point", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ value }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? "Failed to update hours-per-intensity-point");
  }
  return res.json();
}

/**
 * Story 6: set the hours-per-Intensity-point constant. On success, invalidates
 * both the config query and every Solution's workload query — the Story 4
 * benchmark depends on this constant and must reflect the new value on next
 * render (display-only: no Fit Score recalc, no Capacity recheck).
 */
export function useSetHoursPerIntensityPoint() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateHoursPerIntensityPoint,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: systemConfigKeys.all });
      queryClient.invalidateQueries({ queryKey: solutionWorkloadKeys.all });
      toast.success("Hours-per-intensity-point benchmark updated");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

interface SolutionDeletionEnabledResponse {
  enabled: boolean;
}

async function fetchSolutionDeletionEnabled(): Promise<SolutionDeletionEnabledResponse> {
  const res = await fetch("/api/admin/system-config/solution-deletion-enabled");
  if (!res.ok) throw new Error("solution-deletion-enabled unavailable");
  return res.json();
}

/**
 * The firm-wide "Solution deletion enabled" gate (default disabled). Feeds both
 * the System Settings toggle and the delete-affordance visibility on the
 * dashboard + Solution drawer. Consumers should treat loading/error as disabled
 * (fail-closed) — see `useSolutionDeletionAllowed`.
 */
export function useSolutionDeletionEnabled() {
  return useQuery<SolutionDeletionEnabledResponse>({
    queryKey: systemConfigKeys.solutionDeletionEnabled(),
    queryFn: fetchSolutionDeletionEnabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fail-closed boolean for gating delete UI: `true` only once the query has
 * resolved with `enabled: true`. While loading or on error it returns `false`,
 * so a delete affordance never flashes visible before the gate is known.
 */
export function useSolutionDeletionAllowed(): boolean {
  const { data } = useSolutionDeletionEnabled();
  return data?.enabled === true;
}

async function updateSolutionDeletionEnabled(
  enabled: boolean,
): Promise<SolutionDeletionEnabledResponse> {
  const res = await fetch("/api/admin/system-config/solution-deletion-enabled", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ enabled }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? "Failed to update the Solution deletion setting");
  }
  return res.json();
}

/** Toggle the firm-wide Solution deletion gate (RM/CEO — manageConfiguration). */
export function useSetSolutionDeletionEnabled() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateSolutionDeletionEnabled,
    onSuccess: ({ enabled }) => {
      queryClient.invalidateQueries({ queryKey: systemConfigKeys.all });
      toast.success(enabled ? "Solution deletion enabled" : "Solution deletion disabled");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}
